import { ReactNode } from 'react';
import { Card, CardContent } from '../ui/card';
import { formatNumber, formatCurrency, formatPercent } from './dashboardUtils';

interface MetricCardProps {
    icon?: ReactNode;
    title: string;
    value: number;
    target?: number | null;
    variancePct?: number | null;
    format?: 'number' | 'currency';
    subtitle?: ReactNode;
    // when true, negative values are shown as positive (green) and vice versa.
    invertPolarity?: boolean;
    className?: string;
}

// can be used to display a metric with a target and variance percentage
export default function MetricCard({
    icon,
    title,
    value,
    target,
    variancePct,
    format = 'number',
    subtitle,
    invertPolarity = false,
    className,
}: MetricCardProps) {
    const fmt = format === 'currency' ? formatCurrency : formatNumber;

    const hasVariance =
        variancePct !== undefined &&
        variancePct !== null &&
        !Number.isNaN(variancePct);
    const isPositive = hasVariance && (invertPolarity ? variancePct! < 0 : variancePct! >= 0);

    const progressPct =
        target != null && target > 0
            ? Math.min((value / target) * 100, 100)
            : null;

    const isNearTarget = !isPositive && progressPct !== null && progressPct >= 80;

    const badgeClass = isPositive
        ? 'bg-emerald-500/10 text-emerald-500'
        : isNearTarget
          ? 'bg-amber-400/10 text-amber-400'
          : 'bg-red-400/10 text-red-400';

    const barClass = isPositive
        ? 'bg-emerald-500'
        : isNearTarget
          ? 'bg-amber-400'
          : 'bg-red-400';

    return (
        <Card className={`border-subtle ${className ?? ''}`}>
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-subtle-foreground">
                        {icon}
                        {title}
                    </div>
                    {hasVariance && (
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
                        >
                            {formatPercent(variancePct!)}
                        </span>
                    )}
                </div>

                <div className="mt-2 flex items-baseline gap-1.5">
                    <span className="text-2xl font-semibold tracking-tight">
                        {fmt(value)}
                    </span>
                    {target != null && (
                        <span className="text-sm text-subtle-foreground">
                            / {fmt(target)} estimated
                        </span>
                    )}
                </div>

                {subtitle && (
                    <div className="mt-1 text-sm text-subtle-foreground">
                        {subtitle}
                    </div>
                )}

                {progressPct !== null && (
                    <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-subtle">
                        <div
                            className={`h-full rounded-full transition-all ${barClass}`}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
